import axios from 'axios';
import authService from './authService';

const API_ENDPOINT = 'https://m3x53f1ym4.execute-api.us-east-1.amazonaws.com/hci-audio-prod';

// Create axios instance with base URL
const api = axios.create({
    baseURL: API_ENDPOINT,
    timeout: 30000 // 30 seconds timeout
});

// Add request interceptor to add authorization header
api.interceptors.request.use(
    config => {
        const token = authService.getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

// Add response interceptor to handle errors
api.interceptors.response.use(
    response => response,
    error => {
        if (error.response && error.response.status === 401) {
            // Unauthorized - clear token and redirect to login
            localStorage.removeItem('okta-token');
            localStorage.removeItem('okta-id-token');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Fetch folder structure
const fetchFolderStructure = async (prefix = '') => {
    try {
        const response = await api.get('/api/files', {
            params: { 
                for_folder: true,
                prefix 
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching folder structure:', error);
        throw error;
    }
};

// Fetch files for a folder
const fetchFilesForFolder = async (folderPath, page = 1, pageSize = 10000) => {
    try {
        const response = await api.get('/api/files', {
            params: {
                prefix: folderPath,
                page,
                page_size: pageSize
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching files:', error);
        throw error;
    }
};

// Get file download URL
const getFileDownloadUrl = async (filePath) => {
    try {
        // Encode the file path for use in the URL
        const encodedPath = encodeURIComponent(filePath);
        const response = await api.get(`/api/download/${encodedPath}`);
        return response.data.url;
    } catch (error) {
        console.error('Error getting download URL:', error);
        throw error;
    }
};

// Export files data as CSV
const exportFilesAsCsv = async (folderPath, currentPage = 1, pageSize = 20) => {
    try {
        console.log('Searching for CSV files in folder:', folderPath);
        
        // Search for files with CSV extension
        const response = await api.get('/api/files', {
            params: { 
                prefix: folderPath,
                page: 1,
                page_size: 10000 // Get a larger number to find all CSV files
            }
        });
        
        // Extract files data from response
        const allFiles = response.data.files || [];
        
        // Filter only CSV files
        const csvFiles = allFiles.filter(file => file.name.toLowerCase().endsWith('.csv'));
        
        if (csvFiles.length === 0) {
            console.warn('No CSV files found in this folder');
            alert('No CSV files found in this folder.');
            return false;
        }
        
        console.log(`Found ${csvFiles.length} CSV files`);
        
        // If only one CSV file, download it directly
        if (csvFiles.length === 1) {
            const csvFile = csvFiles[0];
            const url = await getFileDownloadUrl(csvFile.name);
            
            // Create and click a temporary download link
            const link = document.createElement('a');
            link.href = url;
            const fileName = csvFile.name.split('/').pop();
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            
            // Clean up
            setTimeout(() => {
                document.body.removeChild(link);
            }, 100);
            
            console.log('CSV file downloaded');
            return true;
        } else {
            // If multiple CSV files, show selection dialog
            let fileList = 'Multiple CSV files found. Select one to download:\n\n';
            
            csvFiles.forEach((file, index) => {
                const fileName = file.name.split('/').pop();
                fileList += `${index + 1}. ${fileName}\n`;
            });
            
            const selection = prompt(fileList + '\nEnter the number of the file to download:');
            
            if (selection && !isNaN(selection)) {
                const selectedIndex = parseInt(selection) - 1;
                
                if (selectedIndex >= 0 && selectedIndex < csvFiles.length) {
                    const selectedFile = csvFiles[selectedIndex];
                    const url = await getFileDownloadUrl(selectedFile.name);
                    
                    // Create and click a temporary download link
                    const link = document.createElement('a');
                    link.href = url;
                    const fileName = selectedFile.name.split('/').pop();
                    link.setAttribute('download', fileName);
                    document.body.appendChild(link);
                    link.click();
                    
                    // Clean up
                    setTimeout(() => {
                        document.body.removeChild(link);
                    }, 100);
                    
                    console.log(`CSV file ${fileName} downloaded`);
                    return true;
                } else {
                    alert('Invalid selection');
                    return false;
                }
            } else {
                console.log('CSV download cancelled by user');
                return false;
            }
        }
    } catch (error) {
        console.error('Error finding CSV files:', error);
        alert('Error finding CSV files: ' + error.message);
        throw error;
    }
};

// Helper function to format file size
const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default {
    fetchFolderStructure,
    fetchFilesForFolder,
    getFileDownloadUrl,
    exportFilesAsCsv
}; 
